import axiosInstance from "./axiosConfig";

/**
 * Service for handling return-related operations
 */
export const returnService = {
  /**
   * Get user's return requests with optional pagination
   * @param {number} page - Page number (1-indexed for API)
   * @param {number} limit - Number of items per page
   * @param {string} status - Optional filter by return status
   * @returns {Promise<Object>} - Promise resolving to paginated return data { returns: [], pagination: {} }
   */
  async getUserReturns(page = 1, limit = 10, status = "") {
    try {
      let url = `/returns/myreturns?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;

      console.log("Calling getUserReturns API:", url);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error(
        "Service Error: Fetching user returns failed:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Get details of a specific return request
   * @param {string} returnId - The return ID to fetch
   * @returns {Promise<Object>} - Promise resolving to return data
   */
  async getReturnById(returnId) {
    try {
      console.log(`getReturnById: Fetching return with ID ${returnId}`);
      const response = await axiosInstance.get(`/returns/${returnId}`);
      console.log("getReturnById: API response:", response.data);
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Fetching return ${returnId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Mark a return as shipped by the user
   * @param {string} returnId - The return ID to mark as shipped
   * @param {Object} data - Optional additional data like tracking info { trackingNumber, shippingCarrier }
   * @returns {Promise<Object>} - Promise resolving to updated return data
   */
  async markShippedByUser(returnId, data = {}) {
    try {
      const response = await axiosInstance.post(
        `/returns/${returnId}/ship`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Marking return ${returnId} as shipped failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Get all return requests for admin view with pagination and filters
   * @param {number} page - Page number (1-indexed for API)
   * @param {number} limit - Number of items per page
   * @param {string} status - Optional filter by return status
   * @param {string} search - Optional search term
   * @returns {Promise<Object>} - Promise resolving to paginated return data { returns: [], pagination: {} }
   */
  async getAdminReturns(page = 1, limit = 10, status = "", search = "") {
    try {
      let url = `/returns?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      console.log("Calling getAdminReturns API:", url);
      const response = await axiosInstance.get(url);
      console.log("API response:", response.data);
      return response.data;
    } catch (error) {
      console.error(
        "Service Error: Fetching admin returns failed:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Admin submits review decisions for a return request
   * @param {string} returnId - The return ID being reviewed
   * @param {Array} itemDecisions - Array of decisions { returnItemId, adminDecision ('approved'/'rejected'), adminQuantity, adminNotes }
   * @param {string} [globalAdminNotes] - Optional overall notes from the admin
   * @returns {Promise<Object>} - Promise resolving to updated return data
   */
  async submitReturnReview(returnId, itemDecisions, globalAdminNotes = "") {
    try {
      const payload = { itemDecisions, globalAdminNotes };
      const response = await axiosInstance.put(
        `/returns/${returnId}/review`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Submitting review for return ${returnId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Admin confirms receipt of returned items
   * @param {string} returnId - The return ID for which items were received
   * @param {Array} itemReceipts - Array of receipt details { returnItemId, receivedQuantity, condition, adminNotes }
   * @param {string} [globalAdminNotes] - Optional overall notes for the receipt
   * @returns {Promise<Object>} - Promise resolving to updated return data
   */
  async markItemsReceivedAdmin(returnId, itemReceipts, globalAdminNotes = "") {
    try {
      const payload = { itemReceipts, globalAdminNotes };
      const response = await axiosInstance.put(
        `/returns/${returnId}/receive`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Marking items received for return ${returnId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Admin processes the refund for a completed return
   * @param {string} returnId - The return ID to process refund for
   * @param {string} refundMethod - The chosen method ('wallet', 'original_payment', 'manual')
   * @param {string} [adminNotes] - Optional notes specific to the refund action
   * @returns {Promise<Object>} - Promise resolving to updated return data
   */
  async processRefundAdmin(returnId, refundMethod, adminNotes = "") {
    try {
      const payload = { refundMethod, adminNotes };
      const response = await axiosInstance.post(
        `/returns/${returnId}/refund`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Processing refund for return ${returnId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },
};

export default returnService;
